/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import Vue from 'vue'
import Vuex from 'vuex'
import modules from './modules'
import * as Toastr from 'toastr'
import i18n from '../i18n/i18n'
import moment from 'moment'
Vue.use(Vuex)
import { captureError, clearSentryUser, setSentryUser } from '../config/sentry'

export default new Vuex.Store({
    state: {
        lang: null,
        langs: ['fr', 'en'],
        baseURL: null,
        toaster: {
            closeButton: false,
            debug: false,
            newestOnTop: true,
            progressBar: true,
            positionClass: 'toast-top-right',
            preventDuplicates: true,
            onclick: null,
            showDuration: 300,
            hideDuration: 100,
            timeOut: 3500,
            extendedTimeOut: 1000,
            showEasing: 'swing',
            hideEasing: 'linear',
            showMethod: 'fadeIn',
            hideMethod: 'fadeOut'
        }
    },
    mutations: {
        setLang(state, lang) {
            localStorage.setItem('lang', JSON.stringify(lang))
            i18n.locale = lang
            state.lang = lang
            moment.locale(lang)
        }
    },
    actions: {
        toast: (state, data) => {
            Toastr.options = state.toaster
            Toastr[data.type](data.message, data.title)
        },
        dispatchError: ({ dispatch }, error) => {
            let traduction = i18n.messages[i18n.locale]
            let title = traduction.error.title
            let message = traduction.error.default_message
            if (error.response && error.response.status) {
                switch (error.response.status) {
                    case 400:
                    case 401:
                        title =
                            error.response.data.message === 'app_not_found'
                                ? traduction.error.app.not_found.title
                                : error.response.data.message
                                ? error.response.data.message
                                : error['title']
                                ? error['title']
                                : traduction.error.title
                        message =
                            error.response.data.message === 'app_not_found'
                                ? traduction.error.app.not_found.message
                                : error.response.data.error ===
                                  'Wrong username or password'
                                ? error['message']
                                : ''
                        break
                    // Voir le commentaire de l'intercepteur dans config/backend.js :
                    // le refus est signalé sur place, il ne fait plus quitter l'écran.
                    case 403:
                        if (error.config && error.config.silence403) return
                        title = traduction.error.forbidden.title
                        message = traduction.error.forbidden.message
                        break
                    case 404:
                        title = error.response.data.message
                            ? error['title']
                            : error.response.data.message
                            ? error.response.data.error
                            : traduction.error.title
                        break
                    case 422:
                        title = traduction.error.unprocessable.title
                        message = `${traduction.error.unprocessable.message} ${message}`
                        captureError(error, {
                            tags: { 'http.status': 422 },
                            extra: {
                                code: '422',
                                url: error.config ? error.config.url : null
                            }
                        })
                        break
                    case 429:
                        message = traduction.error.validation['too_many']
                        title = traduction.error.validation['auto_protection']
                        break
                    // 500 et au-delà (502/503/504…) : erreur serveur remontée à Sentry
                    default:
                        if (error.response.status < 500) break
                        title = `${traduction.error.server.title} ${error.response.status}`
                        message = `${traduction.error.server.message} ${message}`
                        captureError(error, {
                            tags: { 'http.status': error.response.status },
                            extra: {
                                code: error.response.status,
                                url: error.config ? error.config.url : null
                            }
                        })
                        break
                }
            }
            dispatch('toast', {
                type: 'error',
                title: title,
                message: message
            })
        },
        initialiseStore({ state, commit }) {
            var user = localStorage.getItem('user')
            var company = localStorage.getItem('company')
            var token = localStorage.getItem('token')
            var lang = localStorage.getItem('lang')

            if (!user || !company || !token) {
                localStorage.clear()
                clearSentryUser()
                return
            }

            state.auth.token = token
            state.auth.user = JSON.parse(user)
            state.auth.company = JSON.parse(company)

            setSentryUser(state.auth.user, state.auth.company)

            var storedRole = localStorage.getItem('role')
            var storedPermissions = localStorage.getItem('permissions')
            state.auth.role = storedRole ? JSON.parse(storedRole) : null
            state.auth.permissions = storedPermissions ? JSON.parse(storedPermissions) : []

            commit('setLang', lang ? JSON.parse(lang) : state.auth.user.locale)

            var baseURL = `${process.env.VUE_APP_BACKEND_URL}/api/${state.auth.company.id}`

            state.baseURL = baseURL
            localStorage.setItem('baseURL', baseURL)
        },
        prepareParams(context, config) {
            var field = config.sort

            if (config.sortDirection === 'desc') {
                field = '-' + field
            }

            var params = {
                page: config.page,
                include: config.include,
                sort: field
            }

            if (config.filters) {
                for (const [key, value] of Object.entries(config.filters)) {
                    params[`filter[${key}]`] = value
                }
            }

            return params
        },
        scrollTo(context, div) {
            document.getElementById(div).scrollIntoView()
        }
    },
    modules: modules
})
